import { useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { Logo } from './Logo'
import { ThemeToggle } from './ThemeToggle'
import { LanguageChooser } from './LanguageChooser'
import { useI18n } from '../i18n'
import { useAuth } from '../auth/AuthProvider'
import { useMe } from '../api/hooks'

interface Props {
  theme: 'light' | 'dark'
  onToggle: () => void
}

/** The yard-owner space lives under /farm/*; everything else is the horse space. */
const inYardSpace = (pathname: string) => pathname === '/farm' || pathname.startsWith('/farm/')

export function Navbar({ theme, onToggle }: Props) {
  const { pathname } = useLocation()
  const { t } = useI18n()
  const { user } = useAuth()
  const { data: me } = useMe(!!user)
  const onLanding = pathname === '/'
  const isFarmOwner = !!me?.roles?.farmOwner

  return (
    <header className="sticky top-0 z-40 border-b border-brand-100/70 dark:border-brand-800/70 bg-brand-50/80 dark:bg-brand-900/80 backdrop-blur">
      <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-3">
        <Logo />

        <nav className="hidden items-center gap-6 text-sm font-medium text-brand-700 dark:text-brand-200 md:flex">
          {onLanding && !user ? (
            <>
              <Link to="/features" className="transition hover:text-brand-900 dark:hover:text-white">{t('nav.features')}</Link>
              <a href="#how" className="transition hover:text-brand-900 dark:hover:text-white">{t('nav.how')}</a>
              <Link to="/discover" className="transition hover:text-brand-900 dark:hover:text-white">{t('nav.discover')}</Link>
            </>
          ) : user ? (
            <>
              {/* Farm owners work in two spaces → give them a switcher.
                  Horse owners have one space → a plain link. */}
              {isFarmOwner ? (
                <SpaceSwitcher yard={inYardSpace(pathname)} />
              ) : (
                <Link to="/app" className="transition hover:text-brand-900 dark:hover:text-white">{t('nav.myHorses')}</Link>
              )}
              <Link to="/discover" className="transition hover:text-brand-900 dark:hover:text-white">{t('nav.discover')}</Link>
            </>
          ) : (
            <Link to="/discover" className="transition hover:text-brand-900 dark:hover:text-white">{t('nav.discover')}</Link>
          )}
        </nav>

        <div className="flex items-center gap-2 sm:gap-3">
          <LanguageChooser />
          <ThemeToggle theme={theme} onToggle={onToggle} />
          {user ? (
            <UserMenu isFarmOwner={isFarmOwner} />
          ) : (
            <>
              {/* Always visible — a returning user on a phone must find Sign in */}
              <Link to="/signin" className="text-sm font-semibold text-brand-700 dark:text-brand-200 hover:underline">{t('nav.signIn')}</Link>
              <Link to="/signup" className="btn-primary !px-5 !py-2.5">{t('nav.getStarted')}</Link>
            </>
          )}
        </div>
      </div>
    </header>
  )
}

/** Segmented "🐎 My horses | 🏡 My yard" control for dual-space (farm-owner) users. */
function SpaceSwitcher({ yard }: { yard: boolean }) {
  const { t } = useI18n()
  return (
    <div className="flex items-center gap-0.5 rounded-full bg-brand-100/80 dark:bg-brand-800/80 p-1" role="tablist" aria-label={t('nav.spaceSwitch')}>
      <SwitchSeg to="/app" active={!yard} icon="🐎" label={t('farm.switchHorses')} />
      <SwitchSeg to="/farm" active={yard} icon="🏡" label={t('farm.switchFarm')} />
    </div>
  )
}

function SwitchSeg({ to, active, icon, label }: { to: string; active: boolean; icon: string; label: string }) {
  return (
    <Link
      to={to}
      role="tab"
      aria-selected={active}
      aria-current={active ? 'page' : undefined}
      className={`rounded-full px-3 py-1.5 transition ${
        active
          ? 'bg-white dark:bg-brand-700 font-bold text-brand-900 dark:text-white shadow-sm'
          : 'font-semibold text-brand-600 dark:text-brand-300 hover:text-brand-900 dark:hover:text-white'
      }`}
    >
      <span aria-hidden>{icon}</span> {label}
    </Link>
  )
}

function UserMenu({ isFarmOwner }: { isFarmOwner: boolean }) {
  const { t } = useI18n()
  const { signOut } = useAuth()
  const { data: me } = useMe()
  const nav = useNavigate()
  const [open, setOpen] = useState(false)
  const go = (path: string) => () => { setOpen(false); nav(path) }

  return (
    <div className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        aria-label={t('nav.account')}
        aria-haspopup="menu"
        aria-expanded={open}
        className="grid h-11 w-11 place-items-center overflow-hidden rounded-full bg-brand-700 text-sm font-bold text-white"
      >
        {me?.avatarUrl ? <img src={me.avatarUrl} alt="" className="h-full w-full object-cover" /> : '🐎'}
      </button>
      {open && (
        <>
          <button className="fixed inset-0 z-40 cursor-default" aria-hidden tabIndex={-1} onClick={() => setOpen(false)} />
          <div className="absolute right-0 z-50 mt-2 w-56 overflow-hidden rounded-2xl border border-brand-100 dark:border-brand-700 bg-white dark:bg-brand-800 shadow-lift text-sm">
            {/* Primary destinations, ordered by the user's main role */}
            {isFarmOwner ? (
              <>
                <MenuItem onClick={go('/farm')}>🏡 {t('farm.dash.title')}</MenuItem>
                <MenuItem onClick={go('/app')}>🐎 {t('nav.myHorses')}</MenuItem>
                <MenuItem onClick={go('/my-farm')}>📍 {t('farm.my.title')}</MenuItem>
                <MenuItem onClick={go('/discover')}>🔍 {t('nav.discover')}</MenuItem>
              </>
            ) : (
              <>
                <MenuItem onClick={go('/app')}>🐎 {t('nav.myHorses')}</MenuItem>
                <MenuItem onClick={go('/my-farm')}>📍 {t('farm.my.title')}</MenuItem>
                <MenuItem onClick={go('/discover')}>🔍 {t('nav.discover')}</MenuItem>
                <div className="border-t border-brand-100 dark:border-brand-700" />
                <MenuItem onClick={go('/farm/onboarding')}>🏡 {t('farm.onboard.title')}</MenuItem>
              </>
            )}
            {me?.platformAdmin && (
              <>
                <div className="border-t border-brand-100 dark:border-brand-700" />
                <MenuItem onClick={go('/admin')}>🛠 {t('admin.title')}</MenuItem>
              </>
            )}
            <div className="border-t border-brand-100 dark:border-brand-700" />
            <MenuItem onClick={go('/settings')}>{t('nav.settings')}</MenuItem>
            <MenuItem onClick={go('/billing')}>{t('nav.billing')}</MenuItem>
            <div className="border-t border-brand-100 dark:border-brand-700" />
            <MenuItem onClick={async () => { setOpen(false); await signOut(); nav('/') }}>{t('nav.signOut')}</MenuItem>
          </div>
        </>
      )}
    </div>
  )
}

function MenuItem({ onClick, children }: { onClick: () => void; children: React.ReactNode }) {
  return (
    <button onClick={onClick} className="block w-full px-4 py-2.5 text-left font-medium transition hover:bg-brand-100/70 dark:hover:bg-brand-700/60">
      {children}
    </button>
  )
}
